import moment from "moment-timezone";
import { 
  InventoryCatalogCategorySchema, InventoryCatalogSuggestion, ResourceType, ResourceSchema, DayScheduleType, TimeBlock, MembershipSchema, MembershipStatusType, ResourceBookingSchema,
  MemberRecordDropdownOptionsType, BookingStatusType, UATFormData, UATChecklistCategory, UATResponse, WeekdayOptionFields, TeamMemberSchema, CredentialExpirationInfo, PlanRecurrenceOptions, PlanType, PlanSchema, ResourcePlanSchema,
  MemberResourceBookingPlanIdsMap, MemberResourceBookingBalanceMap,
  MemberResourceBookingPlanIdsJsonString, MemberResourceBookingBalanceJsonString,
  EMPTY_MEMBER_BOOKING_PLAN_IDS_JSON, EMPTY_MEMBER_BOOKING_BALANCES_JSON,
  MemberInventoryLimitsMap, MemberInventoryLimitsJsonString, EMPTY_MEMBER_INVENTORY_LIMITS_JSON,
} from "../data/types";
import {TaskAltOutlined, MessageOutlined, PersonRemoveOutlined, DeleteOutlineOutlined, VisibilityOutlined, ThumbDownAltOutlined} from '@mui/icons-material';

import { SortDirection } from '../components/SortableHeader';
import { MeasurementUnitCode, TIME_MEASUREMENT_UNIT_CODES, TimeMeasurementUnitCode } from "./enums";
import { normalizeCommunityPermissions } from "./teamPermissions";


export const INVENTORY_DESCRIPTION_PREVIEW_MAX_LENGTH = 80;
export const INVENTORY_ITEM_DESCRIPTION_MAX_LENGTH = 500;

/**
 * Parses a `MemberResourceBookingPlanIdsJsonString` into a resourceId → planId map.
 * Invalid / empty input safely returns `{}`.
 */
export const parseMemberResourceBookingPlanIds = (
  jsonString?: MemberResourceBookingPlanIdsJsonString | string | null
): MemberResourceBookingPlanIdsMap => {
  try {
    if (!jsonString || jsonString.trim() === '') {
      return {};
    }
    const parsed = JSON.parse(jsonString);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    return parsed as MemberResourceBookingPlanIdsMap;
  } catch (error) {
    if (process.env.NODE_ENV !== 'test') {
      console.error('Error parsing MemberResourceBookingPlanIdsMap from JSON:', error);
    }
    return {};
  }
}

/**
 * Parses a `MemberResourceBookingBalanceJsonString` into a resourceId → balance map.
 * Invalid / empty input safely returns `{}`.
 */
export const parseMemberResourceBookingBalances = (
  jsonString?: MemberResourceBookingBalanceJsonString | string | null
): MemberResourceBookingBalanceMap => {
  try {
    if (!jsonString || jsonString.trim() === '') {
      return {};
    }
    const parsed = JSON.parse(jsonString);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    return parsed as MemberResourceBookingBalanceMap;
  } catch (error) {
    if (process.env.NODE_ENV !== 'test') {
      console.error('Error parsing MemberResourceBookingBalanceMap from JSON:', error);
    }
    return {};
  }
}

/**
 * Serializes a booking plan ids map to the MembershipSchema JSON string form.
 */
export const stringifyMemberResourceBookingPlanIds = (
  memberResourceMap: MemberResourceBookingPlanIdsMap
): MemberResourceBookingPlanIdsJsonString => {
  try {
    return JSON.stringify(memberResourceMap) as MemberResourceBookingPlanIdsJsonString;
  } catch (error) {
    console.error('Error stringifying MemberResourceBookingPlanIdsMap:', error);
    return EMPTY_MEMBER_BOOKING_PLAN_IDS_JSON;
  }
}

/**
 * Serializes a booking balances map to the MembershipSchema JSON string form.
 */
export const stringifyMemberResourceBookingBalances = (
  memberResourceMap: MemberResourceBookingBalanceMap
): MemberResourceBookingBalanceJsonString => {
  try {
    return JSON.stringify(memberResourceMap) as MemberResourceBookingBalanceJsonString;
  } catch (error) {
    console.error('Error stringifying MemberResourceBookingBalanceMap:', error);
    return EMPTY_MEMBER_BOOKING_BALANCES_JSON;
  }
}

/**
 * Parses a `MemberInventoryLimitsJsonString` into an inventoryId → remaining qty map.
 * Invalid / empty input safely returns `{}`.
 */
export const parseMemberInventoryLimits = (
  jsonString?: MemberInventoryLimitsJsonString | string | null
): MemberInventoryLimitsMap => {
  try {
    if (!jsonString || jsonString.trim() === '') {
      return {};
    }
    const parsed = JSON.parse(jsonString);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    const limits: MemberInventoryLimitsMap = {};
    Object.entries(parsed).forEach(([inventoryId, rawQty]) => {
      if (typeof rawQty === 'boolean') {
        return;
      }
      const qty = typeof rawQty === 'number' ? rawQty : Number(rawQty);
      if (Number.isFinite(qty) && qty >= 0) {
        limits[String(inventoryId)] = qty;
      }
    });
    return limits;
  } catch (error) {
    if (process.env.NODE_ENV !== 'test') {
      console.error('Error parsing MemberInventoryLimitsMap from JSON:', error);
    }
    return {};
  }
}

/**
 * Serializes an inventory limits map to the MembershipSchema JSON string form.
 */
export const stringifyMemberInventoryLimits = (
  inventoryLimits: MemberInventoryLimitsMap
): MemberInventoryLimitsJsonString => {
  try {
    return JSON.stringify(inventoryLimits) as MemberInventoryLimitsJsonString;
  } catch (error) {
    console.error('Error stringifying MemberInventoryLimitsMap:', error);
    return EMPTY_MEMBER_INVENTORY_LIMITS_JSON;
  }
}

/**
 * Whether dual-plan inventory allowances should apply for this member + plan.
 */
export const shouldApplyMemberInventoryLimits = (
  plan: PlanSchema | ResourcePlanSchema | null | undefined,
  member?: MembershipSchema | null
): boolean => {
  if (plan?.plan_type !== 'dual') {
    return false;
  }
  const hasPlanInventory = (plan.associated_resources || []).some(
    (association) => (association.inventory || []).length > 0
  );
  const hasMemberLimits = Object.keys(parseMemberInventoryLimits(member?.inventory_limits)).length > 0;
  return hasPlanInventory || hasMemberLimits;
}

/**
 * Plan-configured inventory_id → included max qty for dual plans
 * (mirrors backend `_plan_inventory_limits`).
 */
export const getPlanInventoryLimitsMap = (
  plan: PlanSchema | ResourcePlanSchema | null | undefined
): MemberInventoryLimitsMap => {
  if (plan?.plan_type !== 'dual' || !plan.associated_resources?.length) {
    return {};
  }
  const limits: MemberInventoryLimitsMap = {};
  plan.associated_resources.forEach((association) => {
    (association.inventory || []).forEach((selection) => {
      if (!selection?.inventory_id) {
        return;
      }
      if (!Number.isFinite(selection.max_qty) || selection.max_qty < 0) {
        return;
      }
      const key = String(selection.inventory_id);
      limits[key] = (limits[key] ?? 0) + selection.max_qty;
    });
  });
  return limits;
}

/** Minimal ordered-inventory shape needed to aggregate consumed quantities. */
export type OrderedInventoryQuantity = {
  id?: string
  inventory_id?: string
  total_quantity?: number
}

/**
 * Remaining inventory allowances for booking cost display / edit repricing.
 * For approved bookings, restores this booking's consumed qty (capped at plan max)
 * to match backend `_inventory_limits_for_booking_pricing`.
 */
export const getInventoryLimitsForBookingPricing = (
  member: MembershipSchema | null | undefined,
  plan: PlanSchema | ResourcePlanSchema | null | undefined,
  booking?: {
    status?: string | null
    order_items?: OrderedInventoryQuantity[]
    order_inventory?: OrderedInventoryQuantity[]
  } | null
): MemberInventoryLimitsMap => {
  const configuredLimits = getPlanInventoryLimitsMap(plan);
  if (Object.keys(configuredLimits).length === 0) {
    return parseMemberInventoryLimits(member?.inventory_limits);
  }

  const memberLimits = parseMemberInventoryLimits(member?.inventory_limits);
  const limits: MemberInventoryLimitsMap =
    Object.keys(memberLimits).length > 0
      ? { ...memberLimits }
      : { ...configuredLimits };

  if (!booking || String(booking.status || '').toLowerCase() !== 'approved') {
    return limits;
  }

  const existingItems = booking.order_items ?? booking.order_inventory ?? [];
  const orderedByInventory: MemberInventoryLimitsMap = {};
  existingItems.forEach((item) => {
    const inventoryId = item.inventory_id || item.id;
    if (!inventoryId) {
      return;
    }
    orderedByInventory[inventoryId] =
      (orderedByInventory[inventoryId] ?? 0) + Math.max(0, item.total_quantity || 0);
  });

  Object.entries(orderedByInventory).forEach(([inventoryId, quantity]) => {
    if (!(inventoryId in configuredLimits)) {
      return;
    }
    limits[inventoryId] = Math.min(
      configuredLimits[inventoryId],
      (limits[inventoryId] ?? 0) + quantity
    );
  });

  return limits;
}

export type InventoryCostLineBreakdown = {
  inventoryId: string
  includedQty: number
  billableQty: number
  lineCost: number
  remainingAfterThis: number
  unitPrice: number
  totalQuantity: number
}

/**
 * Allowance-aware inventory line pricing matching backend `_calculate_inventory_cost`:
 * charge only `unit_price * max(0, qty - remaining_limit)`, consuming remaining across lines.
 */
export const calculateInventoryCostLinesWithLimits = (
  lines: Array<{
    inventoryId: string
    totalQuantity: number
    unitPrice?: number
  }>,
  inventoryLimits?: MemberInventoryLimitsMap | null,
  applyLimits = true
): { lines: InventoryCostLineBreakdown[]; inventoryCost: number } => {
  const remainingLimits: MemberInventoryLimitsMap = {};
  if (applyLimits && inventoryLimits) {
    Object.entries(inventoryLimits).forEach(([inventoryId, qty]) => {
      if (Number.isFinite(qty) && qty >= 0) {
        remainingLimits[inventoryId] = qty;
      }
    });
  }

  const breakdown: InventoryCostLineBreakdown[] = lines.map((line) => {
    const unitPrice = line.unitPrice ?? 0;
    const totalQuantity = Math.max(0, line.totalQuantity || 0);
    let includedQty = 0;
    let billableQty = totalQuantity;

    if (applyLimits && line.inventoryId && line.inventoryId in remainingLimits) {
      includedQty = Math.min(totalQuantity, remainingLimits[line.inventoryId]);
      billableQty = totalQuantity - includedQty;
      remainingLimits[line.inventoryId] -= includedQty;
    }

    return {
      inventoryId: line.inventoryId,
      includedQty,
      billableQty,
      lineCost: billableQty * unitPrice,
      remainingAfterThis: line.inventoryId in remainingLimits
        ? remainingLimits[line.inventoryId]
        : 0,
      unitPrice,
      totalQuantity,
    };
  });

  return {
    lines: breakdown,
    inventoryCost: breakdown.reduce((sum, line) => sum + line.lineCost, 0),
  };
}

export type InventoryDescriptionPreview = {
  preview: string;
  isTruncated: boolean;
  fullText: string;
};

export const getInventoryDescriptionPreview = (
  description?: string | null
): InventoryDescriptionPreview => {
  const fullText = description?.trim() ?? "";

  if (!fullText) {
    return { preview: "\u00A0", isTruncated: false, fullText: "" };
  }

  if (fullText.length <= INVENTORY_DESCRIPTION_PREVIEW_MAX_LENGTH) {
    return { preview: fullText, isTruncated: false, fullText };
  }

  const truncated = fullText.slice(0, INVENTORY_DESCRIPTION_PREVIEW_MAX_LENGTH).trimEnd();

  return {
    preview: `${truncated}…`,
    isTruncated: true,
    fullText,
  };
};


export const isTimeMeasurementUnit = (
  unit: MeasurementUnitCode
): unit is TimeMeasurementUnitCode =>
  (TIME_MEASUREMENT_UNIT_CODES as readonly MeasurementUnitCode[]).includes(unit);

export const TIME_MEASUREMENT_UNIT_ADVERBS: Record<TimeMeasurementUnitCode, string> = {
  ms: "millisecond",
  sec: "second",
  min: "minute",
  hr: "hourly",
  day: "daily",
  week: "weekly",
  month: "monthly",
  year: "yearly",
  session: "session",
};

export const getInventoryStockQuantityFieldLabel = (
  measureUnit?: MeasurementUnitCode | string
): string => {
  if (!measureUnit || !isTimeMeasurementUnit(measureUnit as MeasurementUnitCode)) {
    return "Stock quantity";
  }

  const adverbMeasureUnit = TIME_MEASUREMENT_UNIT_ADVERBS[measureUnit as TimeMeasurementUnitCode];
  return `Number of ${adverbMeasureUnit} slots`;
};

export const getInventoryQuantityUnitLabel = (
  measureUnit: MeasurementUnitCode,
  quantity: number
): string => {
  if (isTimeMeasurementUnit(measureUnit)) {
    return quantity > 1 ? "slots" : "slot";
  }
  if (measureUnit === "ea") {
    return quantity > 1 ? "units" : "unit";
  }
  return quantity > 1 ? `${measureUnit}s` : measureUnit;
};

export type MemberSortField =
  | 'full_name'
  | 'username'
  | 'team_name'
  | 'status'
  | 'plan_name'
  | 'member_balance'
  | 'joined';

export const getMemberFullName = (member: MembershipSchema): string =>
  `${member.first_name ?? ''} ${member.last_name ?? ''}`.trim()

export const filterMemberships = (
  members: MembershipSchema[],
  searchTerm: string
): MembershipSchema[] => {
  const term = searchTerm.trim().toLowerCase()
  if (!term) return members

  return members.filter((member) =>
    getMemberFullName(member).toLowerCase().includes(term) ||
    member.username?.toLowerCase().includes(term) ||
    member.email?.toLowerCase().includes(term) ||
    member.team_name?.toLowerCase().includes(term)
  )
}

export const sortMemberships = (
  members: MembershipSchema[],
  field: MemberSortField,
  direction: SortDirection
): MembershipSchema[] => {
  const sorted = [...members]
  const multiplier = direction === 'asc' ? 1 : -1

  sorted.sort((a, b) => {
    let comparison = 0

    switch (field) {
      case 'full_name':
        comparison = getMemberFullName(a).localeCompare(getMemberFullName(b), undefined, { sensitivity: 'base' })
        break
      case 'username':
      case 'team_name':
      case 'status':
      case 'plan_name':
        comparison = (a[field] ?? '').localeCompare(b[field] ?? '', undefined, { sensitivity: 'base' })
        break
      case 'member_balance':
        comparison = (a.member_balance ?? 0) - (b.member_balance ?? 0)
        break
      case 'joined':
        comparison = new Date(a.joined ?? 0).getTime() - new Date(b.joined ?? 0).getTime()
        break
    }

    return comparison * multiplier
  })

  return sorted
}

export const getDefaultMemberSortDirection = (field: MemberSortField): SortDirection =>
  field === 'joined' ? 'desc' : 'asc'

export type AgreementSortField =
  | 'member'
  | 'tier'
  | 'status'
  | 'signatures'
  | 'updated'

export type AgreementSortableRow = {
  memberName: string
  tier: string
  status: string
  signatures: { required: number; completed: number }
  updatedAtMs?: number
}

export const sortAgreementRows = <T extends AgreementSortableRow>(
  rows: T[],
  field: AgreementSortField,
  direction: SortDirection
): T[] => {
  const sorted = [...rows]
  const multiplier = direction === 'asc' ? 1 : -1

  sorted.sort((a, b) => {
    let comparison = 0

    switch (field) {
      case 'member':
        comparison = a.memberName.localeCompare(b.memberName, undefined, { sensitivity: 'base' })
        break
      case 'tier':
        comparison = a.tier.localeCompare(b.tier, undefined, { sensitivity: 'base' })
        break
      case 'status':
        comparison = a.status.localeCompare(b.status, undefined, { sensitivity: 'base' })
        break
      case 'signatures': {
        const aRatio = a.signatures.completed / Math.max(a.signatures.required, 1)
        const bRatio = b.signatures.completed / Math.max(b.signatures.required, 1)
        comparison = aRatio - bRatio
        break
      }
      case 'updated':
        comparison = (a.updatedAtMs ?? 0) - (b.updatedAtMs ?? 0)
        break
    }

    return comparison * multiplier
  })

  return sorted
}

export const getDefaultAgreementSortDirection = (field: AgreementSortField): SortDirection =>
  field === 'updated' ? 'desc' : 'asc'

export type TransactionSortField =
  | 'user_full_name'
  | 'resource_name'
  | 'start_time'
  | 'total_hours'
  | 'plan_rate'
  | 'subtotal_cost'
  | 'total_cost'
  | 'created'
  | 'status'

export const filterTransactions = (
  transactions: ResourceBookingSchema[],
  searchTerm: string
): ResourceBookingSchema[] => {
  const term = searchTerm.trim().toLowerCase()
  if (!term) return transactions

  return transactions.filter((transaction) =>
    transaction.user_full_name?.toLowerCase().includes(term) ||
    transaction.username?.toLowerCase().includes(term) ||
    transaction.resource_name?.toLowerCase().includes(term)
  )
}

export const sortTransactions = (
  transactions: ResourceBookingSchema[],
  field: TransactionSortField,
  direction: SortDirection
): ResourceBookingSchema[] => {
  const sorted = [...transactions]
  const multiplier = direction === 'asc' ? 1 : -1

  sorted.sort((a, b) => {
    let comparison = 0

    switch (field) {
      case 'user_full_name':
      case 'resource_name':
      case 'status':
        comparison = (a[field] ?? '').localeCompare(b[field] ?? '', undefined, { sensitivity: 'base' })
        break
      case 'start_time':
      case 'created':
        comparison = new Date(a[field] ?? 0).getTime() - new Date(b[field] ?? 0).getTime()
        break
      case 'total_hours':
      case 'plan_rate':
      case 'subtotal_cost':
      case 'total_cost':
        comparison = (a[field] ?? 0) - (b[field] ?? 0)
        break
    }

    return comparison * multiplier
  })

  return sorted
}

export const getDefaultTransactionSortDirection = (field: TransactionSortField): SortDirection =>
  field === 'created' ||
  field === 'start_time' ||
  field === 'total_hours' ||
  field === 'plan_rate' ||
  field === 'subtotal_cost' ||
  field === 'total_cost'
    ? 'desc'
    : 'asc'

export const getFullDayName = (day: WeekdayOptionFields): string => {
  const dayMap: Record<WeekdayOptionFields, string> = {
    "sun": "Sunday",
    "mon": "Monday", 
    "tue": "Tuesday",
    "wed": "Wednesday",
    "thu": "Thursday",
    "fri": "Friday",
    "sat": "Saturday"
  }
  return dayMap[day] || capitalizeString(day)
}

// Visual styles for period types
export const getPeriodStyles = (periodType: DayScheduleType['period_type']) => {
  switch (periodType) {
    case 'STANDARD':
      return {
        bg: 'bg-[#EDF7FF]',
        border: 'border-[#BBD7FF]',
        text: 'text-[#2563EB]',
        dot: 'bg-[#2563EB]'
      };
    case 'PEAK':
      return {
        bg: 'bg-[#FFF1E6]',
        border: 'border-[#FFC9A6]',
        text: 'text-[#F3A218]',
        dot: 'bg-[#F3A218]'
      };
    case 'OFF_PEAK':
      return {
        bg: 'bg-emerald-50/70',
        border: 'border-emerald-200',
        text: 'text-[#0F973D]',
        dot: 'bg-[#0F973D]'
      };
    default:
      return {
        bg: 'bg-[#EDF7FF]',
        border: 'border-[#BBD7FF]',
        text: 'text-[#0B5FFF]',
        dot: 'bg-[#0B5FFF]'
      };
      // return {
      //   bg: 'bg-slate-50',
      //   border: 'border-slate-200',
      //   text: 'text-slate-600',
      //   dot: 'bg-slate-400'
      // };
  }
};

export const memberCountPerPlan = (members: MembershipSchema[]) => {
  return members.reduce((acc: { [key: string]: number }, member: MembershipSchema) => {
    // console.log(member.plan_id)
    acc[member.plan_id || ""] = (acc[member.plan_id || ""] || 0) + 1
    return acc
  }, {} as { [key: string]: number })
}

export const isTimeSlot = (value: any): boolean => {
  if (Array.isArray(value)) {
    if (value.length === 0) return false;
    return value.every(v => !!(v && typeof v === 'object' && 'open_time' in v && (v as any).open_time && 'close_time' in v && (v as any).close_time));
  }
  const slot = value as DayScheduleType | undefined;
  return !!(
    slot && typeof slot === 'object' &&
    'open_time' in slot && slot.open_time &&
    'close_time' in slot && slot.close_time
  );
};

export const areBookingsLessThan12Hours = (day: Date, bookings: ResourceBookingSchema[]) => {
  // const startOfDay = moment(day).startOf("day");
  // const endOfDay = moment(day).endOf("day");
  let totalTime = 0; 
  const bookingsInDay = findBookingsForDay(moment(day), bookings);
  for (const booking of bookingsInDay) {
    totalTime += moment(booking.end_time).diff(booking.start_time);
  }
  const test = totalTime < 12 * 60 * 60 * 1000;

  return test;
};


export const areDatesInSameMonth = (date1: Date, date2: Date) => {
  // Extract year and month components from both dates
  const year1 = date1.getFullYear();
  const month1 = date1.getMonth();

  const year2 = date2.getFullYear();
  const month2 = date2.getMonth();

  // Compare the year and month components
  return year1 === year2 && month1 === month2;
};


export const dayAvailability = (day: Date, currentResource: ResourceSchema, nHours: number, bookings: ResourceBookingSchema[]) => {
  const dayOfWeek = moment(day).utc().format("ddd").toLowerCase();
  const raw = (currentResource?.resource_options?.[dayOfWeek] as DayScheduleType | DayScheduleType[] | undefined);
  if (!raw || !isTimeSlot(raw)) {
    return false;
  }
  const blocks = calculateAvailableBlocksForDay(raw, nHours, findBookingsForDay(moment(day), bookings || []));
  return blocks.length > 0;
};


export const findBookingsForDay = (day: moment.Moment, bookings: ResourceBookingSchema[]): Array<ResourceBookingSchema> => {
  const bookingsForDay: Array<ResourceBookingSchema> = [];
  const dayUtc = day.clone().utc();
  if (bookings && bookings.length > 0) {
    for (const booking of bookings) {
      const startTime = moment(booking.start_time).utc();
      const endTime = moment(booking.end_time).utc();

      if (
        startTime.isSameOrBefore(dayUtc, "day") &&
        endTime.isSameOrAfter(dayUtc, "day")
      ) {
        bookingsForDay.push(booking);
      }
    }
  }
  return bookingsForDay;
};


export const calculateAvailableTimeBlocks = (
  openTime: string,
  closeTime: string,
  requestedHours: number,
  bookings: Array<ResourceBookingSchema>
): TimeBlock[] => {
  const blocks: TimeBlock[] = [];
  const startMoment = moment(openTime, "HH:mm A");
  const endMoment = moment(closeTime, "HH:mm A");

  const today = moment().startOf('day');
  startMoment.year(today.year()).month(today.month()).date(today.date());
  endMoment.year(today.year()).month(today.month()).date(today.date());
  
  
  let currentTime = startMoment.clone();
  
  while (currentTime.clone().add(requestedHours, 'hours').isSameOrBefore(endMoment)) {
    const blockEnd = currentTime.clone().add(requestedHours, 'hours');
    let isAvailable = true;
    for (const booking of bookings) {
      const bookingStart = moment(booking.start_time);
      const bookingEnd = moment(booking.end_time);
      
      if (currentTime.isBefore(bookingEnd) && blockEnd.isAfter(bookingStart)) {
        isAvailable = false;
        break;
      }
    }
    
    if (isAvailable) {
      blocks.push({
        start: currentTime.clone(),
        end: blockEnd.clone()
      });
    }
    
    currentTime.add(30, 'minutes');
  }
  
  return blocks;
};

/**
 * Normalize day options to an array of non-overlapping time slots (up to 3)
 */
export const normalizeDayOptions = (value: DayScheduleType | DayScheduleType[] | undefined): DayScheduleType[] => {
  if (!value) return [];
  const slots = Array.isArray(value) ? value : [value];
  // Filter invalid
  const valid = slots.filter(s => isTimeSlot(s)) as DayScheduleType[];
  // Sort by start time
  valid.sort((a, b) => moment(a.open_time, 'HH:mm A').diff(moment(b.open_time, 'HH:mm A')));
  // Ensure non-overlap by removing intersecting ones (keep earliest-first preference)
  const nonOverlapping: DayScheduleType[] = [];
  const seenPeriodTypes = new Set<string>();
  for (const slot of valid) {
    const start = moment(slot.open_time, 'HH:mm A');
    const end = moment(slot.close_time, 'HH:mm A');
    if (!start.isValid() || !end.isValid() || !end.isAfter(start)) continue;
    // Enforce unique period_type if provided
    const period = (slot as any).period_type as string | undefined;
    if (period) {
      if (seenPeriodTypes.has(period)) continue;
    }
    const overlaps = nonOverlapping.some(s => {
      const sStart = moment(s.open_time, 'HH:mm A');
      const sEnd = moment(s.close_time, 'HH:mm A');
      return start.isBefore(sEnd) && end.isAfter(sStart);
    });
    if (!overlaps) nonOverlapping.push(slot);
    if (period) seenPeriodTypes.add(period);
    if (nonOverlapping.length >= 3) break; // up to 3 blocks
  }
  return nonOverlapping;
};

/**
 * Calculate available blocks across multiple day slots
 */
export const calculateAvailableBlocksForDay = (
  dayOptions: DayScheduleType | DayScheduleType[] | undefined,
  requestedHours: number,
  bookings: Array<ResourceBookingSchema>
): TimeBlock[] => {
  const slots = normalizeDayOptions(dayOptions);
  if (slots.length === 0 || requestedHours <= 0) return [];
  let all: TimeBlock[] = [];
  for (const slot of slots) {
    const blocks = calculateAvailableTimeBlocks(slot.open_time, slot.close_time, requestedHours, bookings);
    all = all.concat(blocks);
  }
  // Sort and de-duplicate
  all.sort((a, b) => (a.start as any).valueOf() - (b.start as any).valueOf());
  const unique: TimeBlock[] = [];
  const seen = new Set<string>();
  for (const b of all) {
    const key = `${moment(b.start).format()}-${moment(b.end).format()}`;
    if (!seen.has(key)) {
      unique.push(b);
      seen.add(key);
    }
  }
  return unique;
};


export const generateUATResponse = (formData: UATFormData, isDraft: boolean = false): UATResponse => {
  // Calculate summary statistics
  let totalItems = 0;
  let completedItems = 0;
  let failedItems = 0;
  let excludedItems = 0;

  formData.categories.forEach(category => {
    category.items.forEach(item => {
      if (category.id !== '1') { // Exclude environment setup items
        totalItems++;
        if (item.status === 'successful') completedItems++;
        if (item.status === 'failed') failedItems++;
        if (item.status === 'excluded') excludedItems++;
      }
    });
  });

  const activeItems = totalItems - excludedItems;
  const progress = activeItems > 0 ? (completedItems / activeItems) * 100 : 0;

  // Get device info from environment setup category
  const deviceInfo = {
    device_type: '',
    browser: '',
    operating_system: ''
  };

  const envSetupCategory = formData.categories.find(cat => cat.id === '1');
  if (envSetupCategory) {
    envSetupCategory.items.forEach(item => {
      if (item.title === 'Device Type') deviceInfo.device_type = item.notes || '';
      if (item.title === 'Web Browser') deviceInfo.browser = item.notes || '';
      if (item.title === 'Operating System') deviceInfo.operating_system = item.notes || '';
    });
  }

  // Transform categories to response format
  const responseCategories = formData.categories
    // .filter(category => category.id !== '1') // Exclude environment setup category
    .map(category => ({
      id: category.id,
      title: category.title,
      description: category.description,
      user_type: category.user_type,
      items: category.items.map(item => ({
        id: item.id,
        title: item.title,
        description: item.description,
        status: (category.id !== '1' || isDraft) ? item.status : 'excluded',
        priority: item.priority,
        notes: (category.id !== '1' || isDraft) ? item.notes : ''
      }))
    }));

  return {
    summary: {
      total_items: totalItems,
      completed_items: completedItems,
      failed_items: failedItems,
      excluded_items: excludedItems,
      active_items: activeItems,
      progress_percentage: progress,
      test_environment: formData.test_environment,
      test_date: formData?.test_period?.start_date || moment().format('YYYY-MM-DD'),
      device_info: deviceInfo
    },
    categories: responseCategories as UATChecklistCategory[],
    sign_off_status: formData.sign_off_status
  };
}; 


export const stylePhoneNumber = (phone_number: string) => {
  var cleaned = ('' + phone_number).replace(/\D/g, '')
  var match = cleaned.match(/^(0-1|)?(\d{3})(\d{3})(\d{4})$/)
  // let number;
  if (match) {
      var intlCode = (match[1] ? '+1 ' : '')

      if (phone_number.length === 10){
          phone_number = [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('');
      } else if (phone_number.length > 3 && phone_number.length < 7){
          phone_number = [intlCode, '(', match[2], ') ', match[3]].join('');
      } else if (phone_number.length < 4){
          phone_number = [intlCode, '(', match[2], ')'].join('');
      }

    }
  return phone_number
}

export const zipCodeRegex = /^\d{5}(-\d{4})?$/;

export const planRecurrenceMap = {
  'hourly': 'hour',
  'daily': 'day',
  'weekly': 'week',
  'monthly': 'month',
  'annual': 'year'
}

/** Whether a plan type supports the 24/7 hours access setting */
export const planSupportsFulltimeHours = (planType?: PlanType): boolean =>
  planType === "membership" || planType === "dual";

/** Whether to show the 24/7 access badge on a membership or dual tier card */
export const planShowsFulltimeHoursBadge = (
  plan: Pick<PlanSchema, "is_fulltime_hours" | "plan_type">,
  options?: { defaultPlanType?: PlanType }
): boolean => {
  const planType = plan.plan_type ?? options?.defaultPlanType;
  return Boolean(plan.is_fulltime_hours) && planSupportsFulltimeHours(planType);
}

export const areObjectsEqual = (obj1: any, obj2: any): boolean => {
  // Handle null/undefined cases
  if (obj1 === null || obj1 === undefined) return obj1 === obj2;
  if (obj2 === null || obj2 === undefined) return obj1 === obj2;
  
  // Handle primitive types (strings, numbers, booleans)
  if (typeof obj1 !== 'object' || typeof obj2 !== 'object') {
    return obj1 === obj2;
  }
  
  // Handle arrays
  if (Array.isArray(obj1) || Array.isArray(obj2)) {
    if (!Array.isArray(obj1) || !Array.isArray(obj2)) return false;
    if (obj1.length !== obj2.length) return false;
    return obj1.every((val, index) => areObjectsEqual(val, obj2[index]));
  }
  
  // Handle objects
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  
  if (keys1.length !== keys2.length) return false;
  
  return keys1.every(key => {
    const val1 = obj1[key];
    const val2 = obj2[key];
    
    return areObjectsEqual(val1, val2);
  });
};


// export const fetchRequirementsLookUp = (requirements: RequirementSchema[], memberCredentials: CredentialSchema[], paymentMethods: any[]): CurrentRequirementsSchema[] => {
//   var currentRequirements: any[] = []
//   for (let i=0; i < requirements.length; i++){
//     let requirementData = requirements[i] as CurrentRequirementsSchema
//     // requirementData.isMet = false
//     requirementData.is_met = memberCredentials ? memberCredentials.some((item) => (item["category"] === requirementData.code && item['is_valid'])) : false

//     // isMet: (paymentMethods && paymentMethods.length > 0) ? true : false, 
//     currentRequirements.push(requirementData)
//   }

//   currentRequirements = [
//     ...currentRequirements,
//     {
//       code: 'PAY',
//       name: 'Payment Method',
//       isMet: (paymentMethods && paymentMethods.length > 0) ? true : false

//     }
//   ]
//   return currentRequirements
// }


  // Function to round time to nearest 30 minutes
  export const roundToNearest30Minutes = (inputTime: string) => {
    const momentTime = moment(inputTime, 'HH:mm');
    // Round to nearest 30 minutes
    const minutes = momentTime.minutes();
    const roundedMinutes = minutes < 15 ? 0 : minutes < 45 ? 30 : 0;
    return momentTime.minutes(roundedMinutes).format('HH:mm');
  };


  export const isSameOrLess = (timeString1: string, timeString2: string): boolean => {
    // Handle empty strings or invalid inputs gracefully
    if (!timeString1 || !timeString2) {
      return true; // Return true if either is empty to prevent errors
    }
    
    const moment1 = moment(timeString1, 'hh:mmA');  // Use format string for 'hh:mmA' format
    const moment2 = moment(timeString2, 'hh:mmA');  // Use format string for 'hh:mmA' format
    
    // If either moment is invalid, return true to avoid blocking operations
    if (!moment1.isValid() || !moment2.isValid()) {
      return true;
    }
    
    // Use isBefore or isSame for comparison
    return moment1.isBefore(moment2) || moment1.isSame(moment2);
  }


export const areArraysEqualByProps = <T extends object>(arr1: T[],arr2: T[],keys: (keyof T)[]): boolean => {
  if ((!arr1 && !arr2) || (arr1?.length !== arr2?.length)) {
    return false;
  }
  // Create a string representation of each object with only the specified properties
  const stringify = (obj: T): string => {
    const reduced = keys.reduce((acc, key) => {
      acc[key as string] = obj[key];
      return acc;
    }, {} as Record<string, any>);
    return JSON.stringify(reduced);
  };
  // Convert arrays to sorted strings for comparison
  const sortedStr1 = arr1.map(stringify).sort().join('|');
  const sortedStr2 = arr2.map(stringify).sort().join('|');
  // Compare sorted string representations
  return sortedStr1 === sortedStr2;
}


export const isResourcePath = (path: string) => {
  const resourceTypes: ResourceType[] = ['kitchen', 'documentation', 'training' ,'funding' ,'expert', 'farm']
  // return resourceTypes.some((item: ResourceType) => path.toLowerCase().includes(item.toLowerCase()));
  return resourceTypes.find((item: ResourceType) => path.toLowerCase().includes(item.toLowerCase()));
}


export const filterItemsByCategoryAndName = (
  data: InventoryCatalogCategorySchema[],
  category: string,
  itemName: string,
  limit = 3
): InventoryCatalogSuggestion[] => {
  const normalizedCategory = category.trim().toLowerCase();
  const normalizedQuery = itemName.trim().toLowerCase();

  if (!normalizedCategory || !normalizedQuery || limit <= 0) {
    return [];
  }

  const categoryData = data.find((cat) => cat.category.toLowerCase() === normalizedCategory);
  if (!categoryData?.items?.length) {
    return [];
  }

  const results: InventoryCatalogSuggestion[] = [];

  for (const item of categoryData.items) {
    if (item.name.toLowerCase().includes(normalizedQuery)) {
      results.push({
        ...item,
        id: `${categoryData.category}-${item.name.toLowerCase().replace(/\s+/g, "-")}`,
        category: categoryData.category,
      });
      if (results.length >= limit) {
        break;
      }
    }
  }

  return results;
};


export const getDirectLinkFromGoogleDriveSharedLink = (link: string | undefined): string => {
  // Regular expressions to match S3 and Google Drive shared links
  // const s3Regex = /^https:\/w\/s3\.amazonaws\.com\/.+\/.+$/;
  const googleDriveRegex = /^https:\/\/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)\/view\?usp=sharing$/;
  // console.log("link", link)
  if (link && link.includes('s3')) {
    // If it matches the S3 regex, return the link as is
    // console.log("s3 link", link)
    return link;
  } else if (link && googleDriveRegex.test(link)) {
    // If it matches the Google Drive regex, convert to direct link format
    const match = link.match(googleDriveRegex);
    if (match) {
      const fileId = match[1];
      return `https://drive.google.com/uc?export=view&id=${fileId}`;
    }
  }
  // If neither regex matches, return an empty string
  return '';
}

const getDayName = (dateString: string): string => {
  // Parse the date string
  const date = moment(dateString, 'YYYY-MM-DD');
  // Get the day name
  return date.format('dddd');
}


const getDayOfMonth = (dateString: string): number  => {
  // Parse the date string
  const date = moment(dateString, 'YYYY-MM-DD');
  // Get the day of the month
  return date.date();
}
// import { membershipStatuses } from "../utils/enums";

// const getBookingStatusColor = (status: string) => {
//   if (status) {
//     status = status.toLowerCase();
//     switch (status) {
//       case "in progress":
//         return "#F69E35";
//       case "approved":
//         return "#9CCE5D";
//       case "pending":
//         return "#85bccb";
//       case "canceled":
//         return "#CE5D5D";
//       case "completed":
//         return "#013847";
//       default:
//         return "#38B2AC";
//     }
//   }
//   return "#38B2AC"
// };


const capitalizeString = (string: string) => string?.replace(/\w/, c => c?.toUpperCase())

export const capitalizeWords = (str: string): string => {
  return str.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}


// export type MemberStatusOptionsType = 'pending' | 'verified' | 'declined' | 'inactive' | 'approved' | 'active' | 'removed'

const getMemberStatusOptions = (status: string): MembershipStatusType[] => {
  // var allOptions = membershipStatuses
  if (status) {
    status = status.toLowerCase();
    switch (status) {
      case "pending":
        return [status, "declined"]
      case "verified":
          return [status, "approved", "declined", "removed"]
      case "declined":
        return [status, "approved"]
      case "inactive":
        return [status, "approved"]
      case "approved":
        return [status, "removed"]
      case "active":
        return [status, "removed"]
      case "removed":
        return [status, "declined"]
      default:
        return [status as MembershipStatusType]
    }
  }
  return []
};


export const getBookingStatusLabel = (status: BookingStatusType) => {
  switch (status) {
      case 'pending':
          return 'Pending';
      case 'canceled':
          return 'Cancel';
      case 'approved':
          return 'Approve';
      case 'completed':
          return 'Complete';
      case 'in_progress':
          return 'In Progress';
      default:
          return (status as string).charAt(0).toUpperCase() + (status as string).slice(1);
  }
};



export const getBookingStatusOptions = (resourceType: ResourceType, status: BookingStatusType): BookingStatusType[] => {
  if (status) {
    status = status.toLowerCase() as BookingStatusType;
    switch (status) {
      case "pending":
        return [status, "approved", "canceled"]
      case "approved":
          return [status, "in_progress", "canceled"]
      case "canceled":
        return [status]
      case "in_progress":
        return [status, "canceled", "completed"]
      case "completed":
        return [status]
      default:
        return []
    }
  }
  return []
};



export const getMemberStatusUpdateOptions = (status?: MembershipStatusType): MemberRecordDropdownOptionsType[] => {
  const viewDetails: MemberRecordDropdownOptionsType = {
    label: "View Member",
    value: 'details',
    textColor: "text-[#aaa]",
    bgColor: "bg-white",
    icon: VisibilityOutlined,
  };

  const sendMessage: MemberRecordDropdownOptionsType = {
    label: "Send Message",
    value: 'message',
    textColor: "text-[#aaa]",
    bgColor: "bg-white",
    icon: MessageOutlined,
  };

  if (!status) return [viewDetails];

  switch (status.toLowerCase()) {
    case "pending":
      return [
        // {
        //   label: "Verify",
        //   value: 'verified',
        //   textColor: "text-[#F69E35]",
        //   bgColor: "bg-[#fef5ea]",
        //   icon: VerifiedUserOutlined,
        // },
        {
          label: "Approve",
          value: 'approved',
          textColor: "text-[#b9dc8d]",
          bgColor: "bg-[#E7F5EC]",
          icon: TaskAltOutlined,
        },
        {
          label: "Decline",
          value: 'declined',
          textColor: "text-[#CE5D5D]",
          bgColor: "bg-[#faeeee]",
          icon: ThumbDownAltOutlined,
        },
        viewDetails,
        sendMessage
      ];
    case "verified":
      return [
        {
          label: "Approve",
          value: 'approved',
          textColor: "text-[#b9dc8d]",
          bgColor: "bg-[#E7F5EC]",
          icon: TaskAltOutlined,
        },
        {
          label: "Decline",
          value: 'declined',
          textColor: "text-[#CE5D5D]",
          bgColor: "bg-[#faeeee]",
          icon: ThumbDownAltOutlined,
        },
        {
          label: "Remove",
          value: 'removed',
          textColor: "text-[#668790]",
          bgColor: "bg-[#eee]",
          icon: DeleteOutlineOutlined,
        },
        viewDetails,
        sendMessage
      ];
    case "declined":
    case "inactive":
      return [
        {
          label: "Approve",
          value: 'approved',
          textColor: "text-[#b9dc8d]",
          bgColor: "bg-[#E7F5EC]",
          icon: TaskAltOutlined,
        },
        viewDetails,
        sendMessage
      ];
    case "approved":
      return [
        {
          label: "Activate",
          value: 'active',
          textColor: "text-[#399f48]",
          bgColor: "bg-[#ebf5ec]",
          icon: TaskAltOutlined,
        },
        {
          label: "Deactivate",
          value: 'inactive',
          textColor: "text-[#9a9a9a]",
          bgColor: "bg-[#eee]",
          icon: PersonRemoveOutlined,
        },
        viewDetails,
        sendMessage
      ];
    case "active":
      return [
        {
          label: "Deactivate",
          value: 'inactive',
          textColor: "text-[#9a9a9a]",
          bgColor: "bg-[#eee]",
          icon: PersonRemoveOutlined,
        },
        {
          label: "Remove",
          value: 'removed',
          textColor: "text-[#668790]",
          bgColor: "bg-[#eee]",
          icon: DeleteOutlineOutlined,
        },
        viewDetails,
        sendMessage
      ];
    case "removed":
      return [sendMessage];
    default:
      return [viewDetails, sendMessage];
  }
};

const getMemberStatusColor = (status: string): {bgColor: string, primaryTextColor: string, secondaryTextColor: string} => {
  if (status) {
    status = status.toLowerCase();
    switch (status) {
      case "approved":
        return {bgColor: "#E7F5EC", primaryTextColor: '#b9dc8d', secondaryTextColor: '#819a62'};
      case "active":
        return {bgColor: "#ebf5ec", primaryTextColor: '#399f48', secondaryTextColor: '#afd8b5'};
      case "verified":
        return {bgColor: "#fef5ea", primaryTextColor: '#F69E35', secondaryTextColor: '#fce1c2'};
      case "pending":
        return {bgColor: "#edf5f7", primaryTextColor: '#51a0b5', secondaryTextColor: '#a8cfda'};
      case "declined":
        return {bgColor: "#faeeee", primaryTextColor: '#CE5D5D', secondaryTextColor: '#e6aeae'};
      case "inactive":
        return {bgColor: "#eee", primaryTextColor: '#9a9a9a', secondaryTextColor: '#d6d6d6'};
      case "removed":
        return {bgColor: "#eee", primaryTextColor: '#668790', secondaryTextColor: '#696969'};
      default:
        return {bgColor: "#eee", primaryTextColor: '#bbb', secondaryTextColor: '#696969'};
    }
  }
  return {bgColor: "#eee", primaryTextColor: '#bbb', secondaryTextColor: '#696969'};
};


const getBookingStatusColor = (status: string) => { 
  if (status) {
    status = status.toLowerCase();
    switch (status) {
      case "pending":
        return {primaryTextColor: "#F69E35", bgColor: '#fef5ea', secondaryTextColor: '#fce1c2'};
      case "approved":
        return {primaryTextColor: "#399f48", bgColor: '#ebf5ec', secondaryTextColor: '#afd8b5'};
      case "in_progress":
        return {primaryTextColor: "#51a0b5", bgColor: '#edf5f7', secondaryTextColor: '#a8cfda'};
      case "canceled":
        return {primaryTextColor: "#CE5D5D", bgColor: '#faeeee', secondaryTextColor: '#e6aeae'};
      case "completed":
        return {primaryTextColor: "#013847", bgColor: '#e5ebec', secondaryTextColor: '#51a0b5'};
      default:
        return {primaryTextColor: "#202124", bgColor: '#e8e8e9', secondaryTextColor: '#696969'};
    }
  }
  return {primaryTextColor: "#202124", bgColor: '#e8e8e9', secondaryTextColor: '#696969'};
};


const lightenColor = (hex: string, percent: number): string => {
  const color = parseInt(hex.replace(/^#/, ''), 16);
  const R = ((color >> 16) + percent);
  const G = (((color >> 8) & 0x00FF) + percent);
  const B = ((color & 0x0000FF) + percent);
  const newColor = ((R << 16) | (G << 8) | B).toString(16);
  return `#${newColor.padStart(6, '0')}`;
}


const darkenColor = (hex: string, percent: number): string => {
  const color = parseInt(hex.replace(/^#/, ''), 16);
  const R = ((color >> 16) - percent);
  const G = (((color >> 8) & 0x00FF) - percent);
  const B = ((color & 0x0000FF) - percent);
  const newColor = ((R << 16) | (G << 8) | B).toString(16);
  return `#${newColor.padStart(6, '0')}`;
}


type AnyObject = Record<string, any>; // A type for any object with string keys

const getChangedAttributes = <T extends AnyObject>(
  originalObj: T,
  newObj: T
): T => {
  const changedAttributes: T = {} as T;

  if (newObj){
    originalObj = originalObj ? originalObj as T : {} as T
    for (const key in originalObj) {
      if (originalObj.hasOwnProperty(key)) {
        if (newObj.hasOwnProperty(key) && !areObjectsEqual(newObj[key], originalObj[key])) {
          changedAttributes[key] = newObj[key];
        }
      }
    }

    for (const key in newObj) {
      if (newObj.hasOwnProperty(key) && (!originalObj.hasOwnProperty(key) || !areObjectsEqual(newObj[key], originalObj[key]))) {
          changedAttributes[key] = newObj[key];
        }
    }
  }

  return changedAttributes;
}


const styleThousandCommas = (number: string | number) => {
  if (typeof number === 'number'){
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
  } else if (typeof number === 'string'){
    return number.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
  }
  return number
}


const maxHours = (capacity: number, recurrence: string | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'annual') => {
  if (recurrence && capacity){
    switch (recurrence){
      // case 'daily':
      //   // 24 hrs
      //   return Math.round(16 / capacity)
      case 'weekly':
        // 168 hrs
        return Math.round(115 / capacity)
      case 'monthly':
        // 730 hrs
        return Math.round(500 / capacity)
      case 'annual':
        // 8760 hrs
        return Math.round(6000 / capacity)
      default:
        return 0
    }
  }
  return 0
}

/** Lowercased email or username used to de-duplicate Team Roles rows. */
const getTeamMemberIdentityKey = (member: TeamMemberSchema): string =>
  String(member.email || member.username || "").trim().toLowerCase();

/** Ensures each team member has a unique identity and a stable id for selection UI. */
export const normalizeTeamMembers = (members: TeamMemberSchema[] = []): TeamMemberSchema[] => {
  const seenIds = new Set<string>();
  const seenIdentities = new Set<string>();

  return members.reduce<TeamMemberSchema[]>((uniqueMembers, member, index) => {
    const identityKey = getTeamMemberIdentityKey(member);
    if (identityKey) {
      if (seenIdentities.has(identityKey)) {
        return uniqueMembers;
      }
      seenIdentities.add(identityKey);
    }

    const candidates = [
      member.id?.trim(),
      member.email?.trim(),
      member.username?.trim(),
      `team-${index}`,
    ].filter((value): value is string => Boolean(value));

    let id = candidates[0] ?? `team-${index}`;
    let suffix = 0;
    while (seenIds.has(id)) {
      suffix += 1;
      id = `${candidates[0] ?? `team-${index}`}-${suffix}`;
    }

    seenIds.add(id);
    uniqueMembers.push({
      ...member,
      id,
      permissions: {
        community: {
          expanded: !!member.permissions?.community?.expanded,
          permissions: normalizeCommunityPermissions(member.permissions?.community?.permissions),
        },
      },
    });
    return uniqueMembers;
  }, []);
};

export const getTeamMemberDisplayName = (member: TeamMemberSchema) =>
  [member.first_name, member.last_name].filter(Boolean).join(' ').trim() || member.email;

export const getMembershipDisplayName = (member: MembershipSchema) =>
  [member.first_name, member.last_name].filter(Boolean).join(' ').trim() ||
  member.username ||
  member.email ||
  '';

export const membershipMatchesSearch = (member: MembershipSchema, query: string): boolean => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    member.first_name,
    member.last_name,
    `${member.first_name || ''} ${member.last_name || ''}`,
    member.username,
    member.email,
    member.team_name,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
};

export const resolveMembershipForTeamMember = (
  teamMember: TeamMemberSchema,
  allMemberships: MembershipSchema[]
): MembershipSchema | null => {
  const normalizedEmail = teamMember.email?.trim().toLowerCase();
  const fromList = allMemberships.find(
    (m) =>
      (teamMember.username &&
        (m.username === teamMember.username || m.uid === teamMember.username)) ||
      (normalizedEmail && m.email?.trim().toLowerCase() === normalizedEmail)
  );
  if (fromList?.uid) return fromList;

  if (teamMember.username) {
    return {
      uid: teamMember.username,
      username: teamMember.username,
      email: teamMember.email,
      first_name: teamMember.first_name,
      last_name: teamMember.last_name,
      avatar: teamMember.avatar,
    };
  }

  return null;
};

export {
  getDayName,
  getDayOfMonth,
  capitalizeString,
  maxHours,
  getMemberStatusOptions,
  lightenColor,
  darkenColor,
  getBookingStatusColor,
  getMemberStatusColor,
  getChangedAttributes,
  styleThousandCommas
}

/**
 * Generates a consistent color based on a username or email string
 * @param identifier - username or email string
 * @returns hex color string
 */
export const generateUserColor = (identifier: string): string => {
  if (!identifier) return '#E6E6E6';
  
  // Create a simple hash from the string
  let hash = 0;
  for (let i = 0; i < identifier.length; i++) {
    const char = identifier.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  // Use the hash to generate consistent colors
  // Generate hue between 0-360, saturation between 40-80%, lightness between 60-80%
  const hue = Math.abs(hash) % 360;
  const saturation = 40 + (Math.abs(hash) % 40); // 40-80%
  const lightness = 60 + (Math.abs(hash) % 20); // 60-80%
  
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

/**
 * Extracts initials from first and last name
 * @param firstName - first name
 * @param lastName - last name
 * @param username - fallback to username if names not available
 * @returns 1-2 character uppercase string
 */
export const getInitials = (firstName?: string, lastName?: string, username?: string): string => {
  if (firstName && lastName) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  } else if (firstName) {
    return firstName.charAt(0).toUpperCase();
  } else if (lastName) {
    return lastName.charAt(0).toUpperCase();
  } else if (username) {
    // Extract from email or username
    const cleanUsername = username.split('@')[0]; // Remove domain if email
    return cleanUsername.charAt(0).toUpperCase();
  }
  return '?';
};

/**
 * Check if the current page is running in an iframe
 */
export const isInIframe = (): boolean => {
  try {
    return window !== window.top;
  } catch (e) {
    return true; // If we can't access window.top, we're likely in an iframe
  }
};

/**
 * Check if payment APIs are available in the current context
 */
export const checkPaymentAPIAvailability = async (): Promise<boolean> => {
  try {
    // Check if we're in an iframe (which often blocks payment APIs)
    if (isInIframe()) {
      console.warn('Payment APIs may be restricted in iframe context');
      // Tests expect a false result when in iframe
      return false;
    }

    // Require secure context for payment features (HTTPS or localhost)
    if (typeof window !== 'undefined' && !window.isSecureContext) {
      console.warn('Payment features require a secure context (HTTPS or localhost)');
      return false;
    }

    // Assume allowed if basic checks pass
    return true;
  } catch (error) {
    console.warn('Payment API availability check failed:', error);
    return false;
  }
};

/**
 * Get user-friendly error message for payment permission issues
 */
export const getPaymentPermissionErrorMessage = (context?: string): string => {
  const baseMessage = 'Payment features are not available in this context.';
  
  if (isInIframe()) {
    return `${baseMessage} This page is embedded in an iframe, which may block payment features. Please open this page in a new tab.`;
  }
  
  if (context) {
    return `${baseMessage} ${context}`;
  }
  
  return `${baseMessage} This may be due to browser security policies or iframe restrictions.`;
};

/**
 * Get suggested solutions for payment permission issues
 */
export const getPaymentPermissionSolutions = (): string[] => {
  const solutions = [
    'Open this page in a new tab instead of an iframe',
    'Check your browser\'s security settings',
    'Disable any ad blockers or security extensions',
    'Try a different browser',
    'Ensure you\'re accessing the site over HTTPS'
  ];
  
  if (isInIframe()) {
    solutions.unshift('This page is embedded in an iframe - try opening it directly');
    solutions.push('If embedded, ensure the parent iframe has allow="payment *" allowpaymentrequest');
  }
  
  return solutions;
};

const STRICT_DATE_FORMATS = [
  moment.ISO_8601,
  'YYYY-MM-DD',
  'YYYY-MM-DDTHH:mm:ssZ',
  'YYYY-MM-DDTHH:mm:ss.SSSZ',
];

const parseStrictCalendarDate = (value: string): moment.Moment =>
  moment(value, STRICT_DATE_FORMATS, true);

export const getCredentialExpirationInfo = (
  expiration: string | undefined | null,
  referenceDate?: moment.Moment
): CredentialExpirationInfo => {
  const trimmed = expiration?.trim();
  if (!trimmed) {
    return {
      status: 'none',
      formattedDate: null,
      daysUntilExpiration: null,
      statusLabel: null,
      detailLabel: null,
    };
  }

  const expirationDate = parseStrictCalendarDate(trimmed).startOf('day');
  if (!expirationDate.isValid()) {
    return {
      status: 'none',
      formattedDate: null,
      daysUntilExpiration: null,
      statusLabel: null,
      detailLabel: null,
    };
  }

  const now = (referenceDate ?? moment()).startOf('day');
  const daysUntil = expirationDate.diff(now, 'days');
  const formattedDate = expirationDate.format('MMM D, YYYY');

  if (daysUntil < 0) {
    return {
      status: 'expired',
      formattedDate,
      daysUntilExpiration: daysUntil,
      statusLabel: 'Expired',
      detailLabel: `Expired ${formattedDate}`,
    };
  }

  if (daysUntil <= 30) {
    const detailLabel =
      daysUntil === 0
        ? 'Expires today'
        : daysUntil === 1
          ? 'Expires in 1 day'
          : `Expires in ${daysUntil} days (${formattedDate})`;

    return {
      status: 'expiring_soon',
      formattedDate,
      daysUntilExpiration: daysUntil,
      statusLabel: 'Expiring Soon',
      detailLabel,
    };
  }

  return {
    status: 'valid',
    formattedDate,
    daysUntilExpiration: daysUntil,
    statusLabel: null,
    detailLabel: `Expires ${formattedDate}`,
  };
};

const RECURRENT_BILLING_RECURRENCES = ['weekly', 'monthly', 'annual'] as const;
type RecurrentBillingRecurrence = typeof RECURRENT_BILLING_RECURRENCES[number];

const isRecurrentBillingRecurrence = (
  recurrence: string | undefined | null
): recurrence is RecurrentBillingRecurrence =>
  !!recurrence && RECURRENT_BILLING_RECURRENCES.includes(recurrence as RecurrentBillingRecurrence);

export const getNextBillingCycleDate = (
  subscribedDate: string | undefined | null,
  recurrence: PlanRecurrenceOptions | string | undefined | null,
  referenceDate?: moment.Moment,
  timezone = 'America/New_York'
): moment.Moment | null => {
  if (!subscribedDate?.trim() || !isRecurrentBillingRecurrence(recurrence)) return null;

  const cycleStart = parseStrictCalendarDate(subscribedDate.trim());
  if (!cycleStart.isValid()) return null;

  const cycleStartInTimezone = cycleStart.tz(timezone).startOf('day');

  const now = (referenceDate ?? moment()).tz(timezone).startOf('day');
  const unit = recurrence === 'weekly' ? 'week' : recurrence === 'monthly' ? 'month' : 'year';

  let nextCycleDate = cycleStartInTimezone.clone();
  if (nextCycleDate.isAfter(now)) return nextCycleDate;

  while (nextCycleDate.isBefore(now)) {
    nextCycleDate.add(1, unit);
  }

  return nextCycleDate;
};

export const getNextBillingCycleLabel = (
  subscribedDate: string | undefined | null,
  recurrence: PlanRecurrenceOptions | string | undefined | null,
  planRate: number | undefined | null,
  referenceDate?: moment.Moment,
  timezone = 'America/New_York'
): string | null => {
  const nextCycleDate = getNextBillingCycleDate(subscribedDate, recurrence, referenceDate, timezone);
  if (!nextCycleDate || !isRecurrentBillingRecurrence(recurrence)) return null;

  const prefix =
    planRate && planRate > 0
      ? recurrence === 'weekly'
        ? 'Next billing on'
        : 'Renews on'
      : 'Auto renews on';
  return `${prefix} ${nextCycleDate.format('MMM D, YYYY')}`;
};